import { api, handleServiceError } from './api';

export type EvaluationLevel = 'insatisfatorio' | 'parcial' | 'quase_satisfatorio' | 'satisfatorio';

export type ItemSource = 'ai' | 'manual';
export type EvidenceSource = 'proposal' | 'documents';

export interface ChecklistItem {
  id: number;
  proposal_id: number;
  text: string;
  checked: boolean;
  position: number;
  created_at: string;
  source: ItemSource;
  evidence_source?: EvidenceSource | null;
  item_group?: string | null;
  evaluation_level?: EvaluationLevel | null;
  evaluation_justification?: string | null;
  evaluated_at?: string | null;
  evidencia_texto?: string | null;
  evidencia_pagina?: number | null;
  evidencia_documento_id?: number | null;
}

export interface ChecklistConclusion {
  percentual: number | null;
  nivel: string | null;
  total_itens: number;
  itens_avaliados: number;
  distribuicao: Record<string, number>;
}

export const checklistService = {
  getByProposal: async (proposalId: number): Promise<ChecklistItem[]> => {
    try {
      const response = await api.get(`/checklist/${proposalId}`);
      return response?.data || [];
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'get_by_proposal' });
    }
  },

  create: async (proposalId: number, text: string, position: number, itemGroup?: string | null): Promise<ChecklistItem> => {
    try {
      const response = await api.post(`/checklist/${proposalId}`, { text, checked: false, position, item_group: itemGroup ?? null });
      return response?.data;
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'create' });
    }
  },

  update: async (itemId: number, data: Partial<Pick<ChecklistItem, 'text' | 'checked' | 'position' | 'evaluation_level' | 'item_group'>>): Promise<ChecklistItem> => {
    try {
      const response = await api.patch(`/checklist/item/${itemId}`, data);
      return response?.data;
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'update' });
    }
  },

  delete: async (itemId: number): Promise<void> => {
    try {
      await api.delete(`/checklist/item/${itemId}`);
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'delete' });
    }
  },

  generate: async (proposalId: number, reasoningLevel: string = 'default', useWebSearch: boolean = false, customInstructions?: string | null, layoutDocumentId?: number | null, baseDocumentId?: number | null): Promise<ChecklistItem[]> => {
    try {
      const response = await api.post(`/checklist/${proposalId}/generate`, {
        reasoning_level: reasoningLevel,
        use_web_search: useWebSearch,
        custom_instructions: customInstructions ?? null,
        layout_document_id: layoutDocumentId ?? null,
        base_document_id: baseDocumentId ?? null,
      }, {
        timeout: 180000,
      });
      return response?.data || [];
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'generate' });
    }
  },

  getInstructions: async (proposalId: number): Promise<string | null> => {
    try {
      const response = await api.get(`/checklist/${proposalId}/instructions`);
      return response?.data?.custom_instructions ?? null;
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'get_instructions' });
    }
  },

  evaluate: async (proposalId: number, reasoningLevel: string = 'default', useWebSearch: boolean = false): Promise<ChecklistItem[]> => {
    try {
      const response = await api.post(`/checklist/${proposalId}/evaluate`, { reasoning_level: reasoningLevel, use_web_search: useWebSearch }, {
        timeout: 300000,
      });
      return response?.data || [];
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'evaluate' });
    }
  },

  getConclusion: async (proposalId: number): Promise<ChecklistConclusion | null> => {
    try {
      const response = await api.get(`/checklist/${proposalId}/conclusion`);
      return response?.data ?? null;
    } catch (error) {
      return handleServiceError(error, { feature: 'checklist', action: 'get_conclusion' });
    }
  },
};
